/**
 * The Auto-send tab's Stop-loss step (out of AutoSendTab, 23/10/2026) with
 * its own switch (user: "I want the stop loss to have a toggle. to enable or
 * disable. it will make more sense"): on, the rule card — switched on with
 * no rule, it starts from the usual one; off, one line — the rule is kept,
 * nothing is judged, only the daily cap and Kill stop the day.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { ShieldAlert, Sparkles } from 'lucide-react';
import { type DropGenAutoSendConfig, stopLossOn } from '@trafficpulse/shared';

import { Button, StepCard, Switch } from '@/components/ui';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import type { DraftAutoSend } from '../../../../drop-generator-rule.types';
import { describeStopLoss, STARTER_JOIN, STARTER_ROWS } from '../../../board-header/autosend/settings/autosend-draft';
import { StopLossFields } from '../../../board-header/autosend/settings/StopLossFields';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { HistoryLine } from '../../verdicts/history/HistoryLine';
import { withSuggestion } from '../../verdicts/history/history-words';
import { ReplayPanel } from '../../verdicts/replay/ReplayPanel';

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
  /** The layer as it will be after Save. */
  config: DropGenAutoSendConfig;
  patch: (p: Partial<DraftAutoSend>) => void;
  groupName: string;
  whose: string;
}

const groupHint = (groupName: string) =>
  `Judged every 15 minutes over ${groupName}’s whole day — every drop its containers sent today, all lists and gateways together, SMSWiser's and the tracker's numbers added up, rates recomputed from the totals. A match halts ${groupName} for the rest of the day; the other groups keep sending; a person can resume it earlier.`;

export const StopLossStep: FC<Props> = ({ editor, plan, containerId, config, patch, groupName, whose }) => {
  const d = editor.draft.autoSend;
  const isMaster = editor.draft.kind === 'MASTER';
  const empty = d.rows.length === 0 && !d.advanced;
  const toggle = (on: boolean) => patch(on && empty ? { stopLossEnabled: true, join: STARTER_JOIN, rows: STARTER_ROWS } : { stopLossEnabled: on });
  const summary = !isMaster && !stopLossOn(config) ? `none of its own — ${groupName}’s judges the group` : describeStopLoss(config);
  return (
    <StepCard
      index={3}
      icon={ShieldAlert}
      title="Stop-loss"
      summary={summary}
      headerRight={<Switch checked={d.stopLossEnabled} onCheckedChange={toggle} aria-label="Enable the stop-loss" />}
    >
      {d.stopLossEnabled ? (
        <>
          <StopLossFields
            draft={d}
            onPatch={patch}
            optionalFor={isMaster ? undefined : groupName}
            hint={isMaster ? groupHint(groupName) : `Judged on the drops ${whose} sent today, on the same numbers as the verdicts. Optional — ${groupName}’s rule still judges the whole group.`}
            history={<HistoryLine countryKey={plan.countryKey} id={containerId ?? editor.draft.masterId} kind="container" which="terrible" onUse={(s) => patch({ rows: withSuggestion(d.rows, s) })} />}
          />
          {empty ? (
            <Button size="sm" variant="outline" className="mt-2" onClick={() => patch({ join: STARTER_JOIN, rows: STARTER_ROWS })}>
              <Sparkles className="mr-1 h-3.5 w-3.5" /> Start from the usual rule: delivered under 85 % or bots over 15 %
            </Button>
          ) : null}
          <div className="mt-3">
            <ReplayPanel kind="stopLoss" draft={editor.draft} plan={plan} containerId={containerId} />
          </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="stop-loss-off">
          Off — nothing halts {whose} by itself; only the daily cap and Kill stop the day.
          {empty ? '' : ' Its rule is kept for when you switch it back on.'}
        </p>
      )}
    </StepCard>
  );
};
